import { Row, Table, Checkbox } from "antd";
import React, { useEffect, useMemo, useState } from "react";
import color from "../../resource/color";
import { staticRolesSaleApp } from "../../utility/StaticPermission";
import Button from "../Button/Button";
import Text from "../Text/Text";
import icon from "../../resource/icon";
import styled from "styled-components";

const CardRole = styled.div`
  border: 1px solid ${color.background1};
  margin-bottom: 24px;
`;

export default function RoleSaleApp({ initialMenu, callBack, disabled }: any) {
  const [selectedMenus, setSelectedMenus] = useState(initialMenu || {});
  const [isAllSelected, setIsAllSelected] = useState(false);

  const commonCheckList = useMemo(
    () =>
      Object.keys(staticRolesSaleApp).map((key) => ({
        menuName: key,
        ...staticRolesSaleApp[key as keyof typeof staticRolesSaleApp],
      })),
    [],
  );

  useEffect(() => {
    if (Array.isArray(initialMenu)) {
      const menusSaleApp = initialMenu.reduce((acc, menu) => {
        acc[menu.menuName] = menu.permission;
        return acc;
      }, {});

      setSelectedMenus(menusSaleApp);

      const isAllChecked =
        Object.keys(initialMenu).length === commonCheckList.length &&
        Object.values(initialMenu).every((arr: any) => arr.length > 0);

      setIsAllSelected(isAllChecked);
    }
  }, [initialMenu, commonCheckList]);

  const onClickSelectAll = () => {
    const newIsAllSelected = !isAllSelected;
    setIsAllSelected(newIsAllSelected);

    if (newIsAllSelected) {
      const newSelection: any = commonCheckList.reduce(
        (acc, cur) => {
          const list = cur.list.filter((el: any) => "value" in el).map((el: any) => el.value);
          if (!cur.isNested) {
            return { ...acc, [cur.menuName]: list };
          } else {
            const nestedList = cur.list.reduce((accNested: any, curNested: any) => {
              if ("listNested" in curNested) {
                const nestedValues = curNested.listNested?.map((el: any) => el.value) || [];
                return { ...accNested, [curNested.groupNameNested]: nestedValues };
              }
              return accNested;
            }, {});
            return { ...acc, [cur.menuName]: nestedList };
          }
        },
        {} as Record<string, any>,
      );

      newSelection["history"] = ["view"];
      setSelectedMenus(newSelection);
      callBack(newSelection);
    } else {
      setSelectedMenus({});
      callBack(null);
    }
  };

  const onClearAll = () => {
    setSelectedMenus({});
    setIsAllSelected(false);
    callBack(null);
  };

  const handleMenuChange = (e: any, menuName: string, record: any) => {
    const isChecked = e.target.checked;

    setSelectedMenus((prev) => {
      const updatedMenus: any = { ...prev };
      if (isChecked) {
        if (menuName === "creditLimit") {
          updatedMenus[menuName] = record.list.map((item: any) => item.value);
        } else {
          updatedMenus[menuName] = record.list.map((item: any) => item.value);
        }
      } else {
        delete updatedMenus[menuName];
      }

      updatedMenus["history"] = ["view"];

      const isAllChecked =
        Object.keys(updatedMenus).length === commonCheckList.length &&
        Object.values(updatedMenus).every((arr: any) => arr.length > 0);

      setIsAllSelected(isAllChecked);
      callBack(updatedMenus);
      return updatedMenus;
    });
  };

  const handleActionChange = (e: any, actionValue: any, menuName: string) => {
    const isChecked = e.target.checked;

    setSelectedMenus((prev) => {
      const updatedActions = isChecked
        ? [...(prev[menuName] || []), actionValue]
        : (prev[menuName] || []).filter((item) => item !== actionValue);

      const updatedMenus = { ...prev, [menuName]: updatedActions };

      updatedMenus["history"] = ["view"];
      callBack(updatedMenus);
      return updatedMenus;
    });
  };

  const columns = [
    {
      dataIndex: "menuName",
      key: "menuName",
      width: "40%",
      render: (text: string, record: any) => (
        <div style={{ display: "flex", alignItems: "center" }}>
          <Checkbox
            value={text}
            disabled={text === "เมนูประวัติ" || disabled}
            checked={text === "เมนูประวัติ" || selectedMenus[record.key]?.length > 0}
            onChange={(e) => handleMenuChange(e, record.key, record)}
          />
          <Text fontWeight={700} fontSize={16} style={{ marginLeft: "8px" }}>
            {text}
          </Text>
        </div>
      ),
    },
    {
      key: "actions",
      width: "60%",
      render: (_: any, record: any) => (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
          {record?.key === "creditLimit" &&
            record.list?.map((item: any, index: number) => (
              <Checkbox
                key={item.value}
                value={item.value}
                disabled={disabled}
                checked={selectedMenus[record.key]?.includes(item.value)}
                onChange={(e) => handleActionChange(e, item.value, record.key)}
                style={{ width: index < 4 ? "calc(25% - 8px)" : "100%" }}
              >
                {item.label}
              </Checkbox>
            ))}
        </div>
      ),
    },
  ];

  const dataSource = commonCheckList.map((el) => ({
    key: el.menuName,
    menuName: el.groupName,
    list: el.list,
  }));

  return (
    <CardRole>
      <Row
        style={{
          justifyContent: "space-between",
          padding: 16,
          alignItems: "center",
          backgroundColor: color.background1,
        }}
      >
        <Text fontWeight={700}>
          <img src={icon.sellcodaSale} style={{ width: "28px", height: "28px", marginRight: 8 }} />
          แอปพลิเคชันสำหรับพนักงานขาย (Sales Application)
        </Text>
        {!disabled && (
          <div
            style={{
              display: "flex",
              gap: 16,
              alignItems: "center",
            }}
          >
            <Button
              title='เลือกทั้งหมด'
              style={{
                width: 96,
                padding: "2px 4px",
              }}
              onClick={onClickSelectAll}
              height={32}
            />
            <Button
              title='ล้าง'
              onClick={onClearAll}
              typeButton='primary-light'
              style={{
                width: 96,
                padding: "4px 8px",
              }}
              height={32}
            />
          </div>
        )}
      </Row>
      <Table
        showHeader={false}
        onRow={(record, index) => ({
          style: {
            height: "64px",
          },
        })}
        columns={columns}
        dataSource={dataSource}
        pagination={false}
        rowKey='key'
        size='middle'
      />
    </CardRole>
  );
}
